/**
 * Sentinel Manager — public locator vocabulary for ETL v3 tours.
 *
 * Tour authors reference these aliases via `@plugin.sentinel-manager:NAME`
 * (qualified) or `@:NAME` (active plugin). When the plugin team renames
 * a testid or restructures a wrapper, the vocabulary file is the only
 * thing that changes — every tour referencing the alias keeps working.
 *
 * Naming convention: `<view>.<element>`. Use the kebab style of the
 * plugin's URL segments — keeps grep-ability across PRs.
 *
 * **Reality check (2026-05).** The sentinel-manager plugin's wizard
 * and detail tabs are sparsely instrumented: most form fields carry
 * only an `aria-label` and most tab buttons carry only an `id`
 * attribute. We lean on the locator engine's role+name / label / text
 * / selector strategies for those entries; the dashboard slice has
 * real testids. When the plugin team adds the testids called out in
 * the plan's "follow-up" list, swap the affected entries here — no
 * tour edits required.
 */

import type { TourLocatorVocabulary } from '@empowernow/etl-tour';

export const SENTINEL_MANAGER_LOCATORS: TourLocatorVocabulary = {
  /* ─── Dashboard / fleet (real testids exist) ───────────────────
   * DashboardTab emits a different `data-testid` per render state:
   * `dashboard-root` (loaded fleet), `dashboard-empty` (no sentinels),
   * `dashboard-loading`, `dashboard-error`. The tour's first wait
   * needs to resolve in any of these states, so `dashboard.root`
   * uses a multi-selector CSS match. State-specific aliases below
   * let callers branch on the actual render path.
   */
  'dashboard.root':         { selector: '[data-testid="dashboard-root"], [data-testid="dashboard-empty"], [data-testid="dashboard-loading"], [data-testid="dashboard-error"]' },
  'dashboard.loaded':       { testid: 'dashboard-root' },
  'dashboard.empty':        { testid: 'dashboard-empty' },
  'dashboard.search':       { testid: 'dashboard-search-input' },
  'dashboard.filters':      { testid: 'dashboard-filter-controls' },
  'dashboard.kpi-grid':     { testid: 'kpi-grid' },
  'dashboard.sentinel-grid':{ testid: 'sentinel-grid' },
  // Empty-state CTA — only present when the fleet is empty. Text is
  // "Create Your First Sentinel", distinct from the header's
  // "+ Create Sentinel" so role+name lookups don't collide.
  'dashboard.empty-create': { role: 'button', name: 'Create Your First Sentinel', exact: false },

  /* ─── Create wizard ───────────────────────────────────────────
   * The wizard's form fields don't have testids today — only
   * aria-labels and visible text. These aliases lean on the locator
   * engine's role+name / label / text strategies until the plugin
   * team wires up the testids called out in the follow-up list.
   */
  'wizard.create-btn':         { role: 'button', name: 'Create Sentinel', exact: false },
  'wizard.next-btn':           { text: 'Next', exact: false },
  'wizard.back-btn':           { text: 'Back', exact: false },
  'wizard.create-final-btn':   { text: 'Create Sentinel', exact: false },
  'wizard.step-title-template':{ text: 'Choose a template', exact: false },
  'wizard.step-title-schedule':{ text: 'Configure Schedule', exact: false },
  'wizard.step-title-review':  { text: 'Review', exact: false },

  // The Sentinel Name input has aria-label="Sentinel name" — the
  // locator engine's findByLabel walks both <label for="…"> and
  // aria-label.
  'wizard.sentinel-name':      { label: 'Sentinel name' },

  // Section headings the cursor lands on. Sub-controls (preset pills,
  // time inputs, timezone dropdown) lack stable hooks, so the tour
  // narrates around them rather than driving them directly.
  'wizard.heartbeat-interval': { selector: '[aria-label="Heartbeat interval preset"]' },
  'wizard.active-hours-toggle':{ text: 'Restrict to active hours', exact: false },
  'wizard.skip-weekends':      { text: 'Skip weekends', exact: false },

  /* ─── Workspace (post-create) ──────────────────────────────────
   * The sidebar tabs in WorkspaceTab carry an `id` attribute
   * (`workspace-tab-<key>`) but no `data-testid`. We use a
   * single-attribute CSS selector — the SAFE_SELECTOR_PATTERN
   * allow-list admits `[id="…"]` so this is portable.
   */
  'workspace.tab-overview':      { selector: '[id="workspace-tab-overview"]' },
  'workspace.tab-heartbeat':     { selector: '[id="workspace-tab-heartbeat"]' },
  'workspace.tab-checklist':     { selector: '[id="workspace-tab-checklist"]' },
  'workspace.tab-notifications': { selector: '[id="workspace-tab-notifications"]' },
  'workspace.tab-memory':        { selector: '[id="workspace-tab-memory"]' },
  'workspace.tab-files':         { selector: '[id="workspace-tab-files"]' },

  // Detail-page actions
  'detail.run-doctor':           { text: 'Run Doctor', exact: false },
};
